
import React, { useState, useMemo } from 'react';
import { X, Users, Search, Plus, Trash2, Check, Download } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/Button';
import Papa from 'papaparse';

interface EngagementRosterManagerProps {
  engagementId: string;
  onClose: () => void;
}

export const EngagementRosterManager: React.FC<EngagementRosterManagerProps> = ({ engagementId, onClose }) => {
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: participation, isLoading: loadingRoster } = useQuery({
    queryKey: ['engagementRoster', engagementId],
    queryFn: async () => {
      const { data, error } = await supabase.from('participation').select('*, people(*)').eq('engagement_id', engagementId);
      if (error) throw error;
      return data;
    }
  });

  const sortedParticipation = useMemo(() => {
    if (!participation) return [];
    return [...participation].sort((a, b) => {
      const lastA = (a.people?.last_name || '').toLowerCase();
      const lastB = (b.people?.last_name || '').toLowerCase();
      if (lastA < lastB) return -1;
      if (lastA > lastB) return 1;
      return (a.people?.first_name || '').localeCompare(b.people?.first_name || '');
    });
  }, [participation]);

  const handleExport = () => {
    const data = sortedParticipation.map(p => ({
      'First Name': p.people?.first_name,
      'Last Name': p.people?.last_name,
      'Email': p.people?.email || '—',
      'Status': p.status
    }));
    const csv = Papa.unparse(data);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `engagement_roster_${engagementId}.csv`);
    link.click();
  };

  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ['peopleSearch', searchTerm],
    queryFn: async () => {
      if (searchTerm.length < 2) return [];
      const { data } = await supabase.from('people').select('*').or(`first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`).limit(5);
      return data || [];
    },
    enabled: searchTerm.length >= 2
  });

  const addParticipantMutation = useMutation({
    mutationFn: async (personId: string) => {
      await supabase.from('participation').insert({
        engagement_id: engagementId,
        person_id: personId,
        status: 'active',
        created_by_email: profile?.email
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagementRoster', engagementId] });
      queryClient.invalidateQueries({ queryKey: ['engagement-roster-count', engagementId] });
    }
  });

  const removeParticipantMutation = useMutation({
    mutationFn: async (id: string) => {
      await supabase.from('participation').delete().eq('id', id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['engagementRoster', engagementId] });
      queryClient.invalidateQueries({ queryKey: ['engagement-roster-count', engagementId] });
    }
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-2xl border border-slate-100 overflow-hidden flex flex-col max-h-[85vh] animate-in zoom-in-95 duration-200">
        <div className="p-8 border-b border-slate-100 flex items-center justify-between">
          <h2 className="text-xl font-black uppercase text-slate-900 tracking-tight flex items-center gap-3">
            <Users size={24} className="text-blue-600" />
            Manage Program Roster
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 transition-colors">
            <X size={24} />
          </button>
        </div>

        <div className="p-8 border-b border-slate-50 bg-slate-50/30 space-y-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
            <input 
              type="text" 
              placeholder="Query directory for identities..."
              className="w-full pl-12 pr-4 py-4 bg-white border border-slate-200 rounded-lg outline-none focus:ring-4 focus:ring-slate-100 transition-all font-bold text-slate-900 shadow-inner"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>

          {searchTerm.length >= 2 && (
            <div className="bg-white border border-slate-100 rounded-lg shadow-xl overflow-hidden divide-y divide-slate-50">
              {searching ? (
                <div className="p-4 text-center text-[10px] font-black uppercase text-slate-400 animate-pulse">Consulting Database...</div>
              ) : searchResults?.length === 0 ? (
                <div className="p-4 text-center text-[10px] font-black uppercase text-slate-400">Zero matches found</div>
              ) : (
                searchResults?.map(person => {
                  const isAdded = participation?.some(p => p.person_id === person.id);
                  return (
                    <div key={person.id} className="p-4 flex items-center justify-between group hover:bg-slate-50">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-slate-900 text-[#FFDB00] rounded-lg flex items-center justify-center font-black text-[10px]">{person.first_name[0]}{person.last_name[0]}</div>
                        <div>
                          <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{person.first_name} {person.last_name}</p>
                          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{person.email}</p>
                        </div>
                      </div>
                      <button 
                        disabled={isAdded || addParticipantMutation.isPending}
                        onClick={() => addParticipantMutation.mutate(person.id)}
                        className={`p-2 rounded-lg transition-all ${isAdded ? 'text-emerald-500 bg-emerald-50' : 'text-slate-200 hover:text-slate-900 hover:bg-slate-100'}`}
                      >
                        {isAdded ? <Check size={16} strokeWidth={3} /> : <Plus size={16} strokeWidth={3} />}
                      </button>
                    </div>
                  )
                })
              )}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-8 space-y-4">
          <div className="flex items-center justify-between px-2 mb-2">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">Active Enrollees ({participation?.length || 0})</h3>
            <Button variant="outline" className="h-7 px-3 text-[8px]" icon={<Download size={12} />} onClick={handleExport}>Export Roster</Button>
          </div>
          {loadingRoster ? (
             <div className="p-10 text-center text-slate-300 animate-pulse uppercase font-black tracking-widest text-[10px]">Syncing manifest...</div>
          ) : sortedParticipation?.length === 0 ? (
             <div className="p-12 text-center border border-dashed border-slate-200 rounded-lg flex flex-col items-center gap-3">
               <Users size={32} className="text-slate-100" />
               <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest text-center">Roster is currently empty.</p>
             </div>
          ) : (
            <div className="grid grid-cols-1 gap-2">
              {sortedParticipation?.map(p => (
                <div key={p.id} className="flex items-center justify-between p-4 bg-white border border-slate-100 rounded-lg group hover:border-slate-300 transition-all shadow-sm">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-100 text-slate-900 rounded-lg flex items-center justify-center font-black text-xs uppercase">{p.people?.first_name[0]}{p.people?.last_name[0]}</div>
                    <div>
                      <p className="text-sm font-black text-slate-900 uppercase tracking-tight leading-none mb-1">{p.people?.first_name} {p.people?.last_name}</p>
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{p.people?.email}</p>
                    </div>
                  </div>
                  <button onClick={() => { if(confirm('Remove from roster?')) removeParticipantMutation.mutate(p.id); }} className="p-2 text-slate-200 hover:text-rose-500 transition-all"><Trash2 size={18} /></button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-8 border-t bg-slate-50 flex justify-end">
           <Button variant="ghost" onClick={onClose}>Finish Management</Button>
        </div>
      </div>
    </div>
  );
};
